import Chip from "@mui/material/Chip";
import { useTranslation } from "i18n-fe";
import { OTHER_CAUSE_LABEL_KEY, isKnownFvdPushCause } from "../util/fvdPushError";

// Card #1608 — AC1's "distinguishing a locked .sdf from a genuine failure":
// foxbox's PushStatusReport.cs sets `cause` to "locked" (a transient hold on
// the .sdf, operator action is to unlock/retry) or "unknown" (a genuine
// write failure with no positively-identified cause — reporting anything
// more specific would fabricate a diagnosis the data doesn't support, see
// that file's own header comment). `cause: null` covers this card's
// degrade-gracefully case (an older row, or any writer that isn't foxbox's
// JSON shape) — shown as a plain outlined chip rather than guessing.
//
// Semantic MUI Chip colors only, mirroring SheetSentChip/SheetStateChip —
// never `color="primary"`, which is the brand darkGrey and near-invisible in
// dark mode (see ThemeBrand.js's #813/#816 comments).
const CAUSE_DISPLAY = {
  locked: { color: "warning", variant: "filled", labelKey: "reviewQueue.fvdFailures.cause.locked" },
  unknown: { color: "error", variant: "filled", labelKey: "reviewQueue.fvdFailures.cause.unknown" },
  // #2164: causes foxbox positively identifies (PushStatusReport.Map). The
  // operator action differs from "locked" (which self-retries): no_points_basis
  // = a confirmed sheet produced no score, go look at the sheet; no_fvd_match
  // = the ride never resolved to an FVD row (#1897); write_not_verified = the
  // write claimed success but the read-back disagreed (#2390).
  no_points_basis: { color: "warning", variant: "filled", labelKey: "reviewQueue.fvdFailures.cause.noPointsBasis" },
  no_fvd_match: { color: "warning", variant: "filled", labelKey: "reviewQueue.fvdFailures.cause.noFvdMatch" },
  write_not_verified: {
    color: "error",
    variant: "filled",
    labelKey: "reviewQueue.fvdFailures.cause.writeNotVerified",
  },
  // #1609: the pass could not open the show's .sdf at all (FVD desktop holding
  // it) before it could resolve ride ids, so nothing was attempted. Transient
  // and show-wide: it self-heals on the next pass once the file opens.
  fvd_unavailable: { color: "warning", variant: "filled", labelKey: "reviewQueue.fvdFailures.cause.fvdUnavailable" },
};

export default function FvdPushFailureCauseChip({ cause, size = "small", ...props }) {
  const { t } = useTranslation("scoreadmin");
  // #2164: a well-formed cause the FE has no entry for renders "Other
  // (<cause>)", outlined; only a null cause is "Unclassified".
  const known = isKnownFvdPushCause(cause);
  const display = known
    ? CAUSE_DISPLAY[cause]
    : cause
      ? { color: "default", variant: "outlined", labelKey: OTHER_CAUSE_LABEL_KEY }
      : { color: "default", variant: "outlined", labelKey: "reviewQueue.fvdFailures.cause.unclassified" };
  return (
    <Chip
      size={size}
      color={display.color}
      variant={display.variant}
      label={t(display.labelKey, { cause })}
      data-testid="fvd-push-failure-cause-chip"
      data-fvd-push-failure-cause={cause ?? "unclassified"}
      {...props}
    />
  );
}
